import { formatMoney } from './currency';
import { displayBrand, findEnumLabel } from './listingFieldLabels';

// What a link preview card has room for.
const SNIPPET_MAX = 150;

// A middot with spaces, the way a product card lists its facts.
const SEPARATOR = ' \u00b7 ';

// Size lives under its own key per category ("storrelse-klar" for clothes,
// "storrelse-sko" for shoes), so a listing carries at most one of them.
const SIZE_KEY_PREFIX = 'storrelse-';

/**
 * Cut text to a preview-sized snippet on a word boundary, with an ellipsis
 * when something was cut. Whitespace is collapsed first — listing text often
 * has line breaks.
 */
export const snippet = (text, max = SNIPPET_MAX) => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) {
    return clean;
  }
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:–-]+$/, '')}…`;
};

/** "Str. 40" / "Str. M", from whichever size field the listing has. */
const sizePart = (listingFields, publicData, intl) => {
  const key = Object.keys(publicData || {}).find(
    k => k.startsWith(SIZE_KEY_PREFIX) && publicData[k]
  );
  const size = key ? findEnumLabel(listingFields, key, publicData[key]) : null;
  return size ? intl.formatMessage({ id: 'ListingPage.socialSharingSize' }, { size }) : null;
};

/** "Pent brukt", "Som ny" — the condition, as the listing field labels it. */
const conditionPart = (listingFields, publicData) =>
  findEnumLabel(listingFields, 'tilstand', publicData?.tilstand);

/**
 * The price, but only in the marketplace's own currency. A listing priced in
 * anything else formats as a bare currency code, which says nothing on a
 * shared card.
 */
const pricePart = (price, marketplaceCurrency, intl) =>
  price && price.currency === marketplaceCurrency ? formatMoney(intl, price) : null;

/**
 * og:description for a listing: what a buyer scans on a shared card, as a
 * single compact line of facts.
 *
 * "Camilla Pihl · Str. 40 · Pent brukt · 600 kr"
 *
 * Middots rather than full stops, and no stop at the end: seen beside H&M's
 * and Vakre Vene's share cards, sentences read like prose where this wants to
 * read like a product card (Guro, Sept 2026).
 *
 * Condition is in. It was deliberately left out of the first version — third
 * in line after brand and price, "Pent brukt" read as pre-negotiating rather
 * than inviting — but Guro asked for it back once she saw the card in a feed,
 * and she is right that on a second-hand marketplace it is a selling point
 * rather than a caveat.
 *
 * Every part is optional and missing ones are skipped rather than left as an
 * empty separator, so this reads sensibly from a listing with only a price up
 * to one with everything filled. A listing with nothing structured at all
 * falls back to a snippet of the seller's own text.
 *
 * @param {Object} params
 * @param {Array} params.listingFields - listing field configuration, for the enum labels
 * @param {Object} params.publicData - the listing's publicData
 * @param {string} [params.description] - the seller's own text, used as a fallback
 * @param {Object} [params.price] - Money
 * @param {string} [params.marketplaceCurrency]
 * @param {Object} params.intl
 * @returns {string|undefined}
 */
export const socialDescription = ({
  listingFields,
  publicData,
  description,
  price,
  marketplaceCurrency,
  intl,
}) => {
  const parts = [
    displayBrand(listingFields, publicData),
    sizePart(listingFields, publicData, intl),
    conditionPart(listingFields, publicData),
    pricePart(price, marketplaceCurrency, intl),
  ].filter(Boolean);

  if (parts.length > 0) {
    return parts.join(SEPARATOR);
  }

  return snippet(description) || undefined;
};
